import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fbEvent } from "@/lib/pixel";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Heart, ShoppingBag, Truck, RefreshCw, ShieldCheck, Zap, ChevronRight, MessageCircle, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StickyBuyBar from "@/components/StickyBuyBar";

export default function Product() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({});
  const [selected, setSelected] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const { addToCart, toggleWishlist, isWishlisted } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${slug}`).then(({data}) => {
      setData(data); setSelected(0); setQty(1);
      const p = data?.product;
      if (p) fbEvent("ViewContent", {
        content_ids: [p.sku || p.product_id],
        content_name: p.name,
        content_type: "product",
        value: Number(p.price) || 0,
        currency: "INR",
      });
    });
    api.get("/settings").then(({data}) => setSettings(data));
  }, [slug]);

  useEffect(() => {
    if (!lightbox) return;
    const h = (e) => { if (e.key === "Escape") setLightbox(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [lightbox]);

  if (!data) return <div className="container-x py-20 text-center text-muted-foreground">Loading…</div>;
  const { product: p, related } = data;
  const variants = (p.variants && p.variants.length) ? p.variants : null;
  const activeVariant = variants
    ? (variants.find((v) => v.variant_id === selectedVariantId) || variants[0])
    : null;
  const sizeOptions = !variants && p.size_options && Object.keys(p.size_options).length ? p.size_options : null;
  const designOptions = p.design_options && p.design_options.length ? p.design_options : null;
  const activeSize = activeVariant ? activeVariant.size : (selectedSize || (sizeOptions ? Object.keys(sizeOptions)[0] : null));
  const activeDesign = designOptions ? (selectedDesign || designOptions[selected] || designOptions[0]) : null;
  const activePrice = activeVariant ? activeVariant.price : (sizeOptions && activeSize ? sizeOptions[activeSize] : p.price);
  const mrpOptions = !variants && p.mrp_options && Object.keys(p.mrp_options).length ? p.mrp_options : null;
  const activeMrp = activeVariant
    ? (activeVariant.mrp || activeVariant.price)
    : (mrpOptions && activeSize && mrpOptions[activeSize] != null ? mrpOptions[activeSize] : p.mrp);
  const discount = activeMrp > activePrice ? Math.round(((activeMrp - activePrice) / activeMrp) * 100) : 0;
  const savings = activeMrp > activePrice ? Math.round(activeMrp - activePrice) : 0;
  const unitLabel = (activeVariant && activeVariant.unit_label) || p.unit_label || "";
  const activeDimensions = (activeVariant && activeVariant.dimensions) || "";
  const activeStock = activeVariant ? (activeVariant.stock || 0) : p.stock;
  const nameForCart = [p.name, activeSize && `(${activeSize})`, activeDesign && `· ${activeDesign}`].filter(Boolean).join(" ");
  const priceForCart = { ...p, price: activePrice, mrp: activeMrp, name: nameForCart, image: p.images?.[selected] || (p.images || [])[0], unit_label: unitLabel, dimensions: activeDimensions };
  const cartSize = activeVariant ? activeVariant.size : (sizeOptions ? activeSize : null);
  const cartDesign = designOptions ? activeDesign : null;
  const cartVariantId = activeVariant ? activeVariant.variant_id : null;
  const pickDesign = (label) => {
    setSelectedDesign(label);
    if (designOptions) {
      const idx = designOptions.indexOf(label);
      if (idx >= 0 && p.images && p.images[idx]) setSelected(idx);
    }
  };
  const buyNow = () => { addToCart(priceForCart, qty, cartSize, cartDesign, cartVariantId); nav("/checkout"); };

  const waNumber = (settings.whatsapp_number || "").replace(/\D/g, "");
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hello AdiYogi Handicrafts,\nI am interested in:\n${p.name}\nProduct Link: ${typeof window !== "undefined" ? window.location.href : ""}`)}` : null;

  const productSchema = {
    "@context": "https://schema.org", "@type": "Product",
    name: p.name, image: p.images, description: p.description,
    sku: p.sku, brand: { "@type": "Brand", name: "AdiYogi Handicrafts" },
    offers: {
      "@type": "Offer", url: typeof window !== "undefined" ? window.location.href : "",
      priceCurrency: "INR", price: p.price,
      availability: p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    },
  };
  const bcSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? window.location.origin : "" },
      { "@type": "ListItem", position: 2, name: p.category, item: `${typeof window !== "undefined" ? window.location.origin : ""}/shop?category=${p.category}` },
      { "@type": "ListItem", position: 3, name: p.name },
    ],
  };

  return (
    <div className="container-x py-8 md:pb-8 pb-40" data-testid="product-page">
      <SEO
        title={p.seo_title || p.name}
        description={p.seo_description || p.short_description}
        image={p.images?.[0]}
        schema={{ "@context": "https://schema.org", "@graph": [productSchema, bcSchema] }}
      />
      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-6 flex-wrap">
        <Link to="/" className="hover:gold-text">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={`/shop?category=${p.category}`} className="hover:gold-text capitalize">{p.category.replace(/-/g, " ")}</Link>
        {p.subcategory && (<><ChevronRight className="w-3 h-3" /><Link to={`/shop?category=${p.category}&subcategory=${encodeURIComponent(p.subcategory)}`} className="hover:gold-text">{p.subcategory}</Link></>)}
        <ChevronRight className="w-3 h-3" />
        <span>{p.name}</span>
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <button onClick={() => setLightbox(true)} className="block aspect-[5/4] md:aspect-square rounded-xl overflow-hidden border hairline bg-muted mb-3 w-full group" aria-label="Open full image" data-testid="open-lightbox">
            <img src={p.images?.[selected]} alt={p.image_alt || p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid="product-main-image" />
          </button>
          {/* Mobile compact price strip — surfaces name/price above thumbnails */}
          <div className="md:hidden mb-3" data-testid="pdp-mobile-price-strip">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{p.material}</div>
            <div className="font-serif text-lg leading-tight mt-0.5">{p.name}</div>
            <div className="flex items-baseline gap-2 flex-wrap mt-1">
              <span className="font-serif text-2xl gold-text">₹{activePrice}</span>
              {activeMrp > activePrice && <span className="text-sm text-muted-foreground line-through">₹{activeMrp}</span>}
              {discount > 0 && <span className="text-[10px] bg-espresso text-ivory px-2 py-0.5 rounded-full">-{discount}%</span>}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">Free shipping ₹1,999+ · Prepaid UPI · 3–4 day delivery</div>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {p.images?.map((im, i) => (
              <button key={i} onClick={() => setSelected(i)} className={`aspect-square rounded overflow-hidden border ${i===selected ? "border-[#B8860B]" : "hairline"}`} data-testid={`thumb-${i}`} aria-label={`View image ${i+1}`}>
                <img src={im} alt={`${p.name} — view ${i+1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">{p.material}</div>
          <h1 className="font-serif text-3xl md:text-4xl leading-tight" data-testid="product-title">{p.name}</h1>
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <span>SKU {p.sku}</span>
          </div>
          <div className="mt-6 flex items-baseline gap-3 flex-wrap">
            <span className="font-serif text-4xl gold-text" data-testid="product-price">₹{activePrice}</span>
            {unitLabel && <span className="text-sm text-espresso font-medium" data-testid="product-unit-label">{unitLabel}</span>}
            {activeMrp > activePrice && <span className="text-lg text-muted-foreground line-through" data-testid="product-mrp">₹{activeMrp}</span>}
            {discount > 0 && <span className="text-xs bg-espresso text-ivory px-2 py-1 rounded-full">-{discount}%</span>}
          </div>
          {savings > 0 && (
            <div className="mt-1 text-xs text-emerald-700 font-medium" data-testid="product-savings">You save ₹{savings}</div>
          )}
          {designOptions && (
            <div className="mt-5" data-testid="design-picker">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Select Design</div>
              <div className="flex flex-wrap gap-2">
                {designOptions.map((label, i) => (
                  <button
                    key={label}
                    onClick={() => pickDesign(label)}
                    className={`px-4 py-2 rounded-full text-sm border transition ${activeDesign === label ? "bg-espresso text-ivory border-espresso" : "bg-white hairline hover:border-[#B8860B]"}`}
                    data-testid={`design-${label.replace(/\s+/g,'-').toLowerCase()}`}
                  >
                    <span className="font-medium">{label}</span>
                  </button>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">Selected: <span className="text-espresso font-medium">{activeDesign}</span> — image updates to match your choice.</div>
            </div>
          )}
          {sizeOptions && (
            <div className="mt-4" data-testid="size-picker">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Select Size</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(sizeOptions).map(([sz, pr]) => (
                  <button
                    key={sz}
                    onClick={() => setSelectedSize(sz)}
                    className={`px-4 py-2 rounded-full text-sm border transition ${activeSize === sz ? "bg-[#B8860B] text-ivory border-[#B8860B]" : "bg-white hairline hover:border-[#B8860B]"}`}
                    data-testid={`size-${sz.replace(/\s+/g,'-')}`}
                  >
                    <span className="font-medium">{sz}</span>
                    <span className={`ml-2 text-xs ${activeSize === sz ? "opacity-90" : "text-muted-foreground"}`}>₹{pr}{unitLabel ? ` ${unitLabel}` : ""}</span>
                  </button>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">{unitLabel ? `All prices are ${unitLabel}. ` : ""}Price varies according to size, design and number of items in the set.</div>
            </div>
          )}
          {variants && (
            <div className="mt-4" data-testid="variant-picker">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Select Size</div>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => {
                  const active = (activeVariant && activeVariant.variant_id === v.variant_id);
                  const oos = (v.stock || 0) <= 0;
                  return (
                    <button
                      key={v.variant_id}
                      onClick={() => !oos && setSelectedVariantId(v.variant_id)}
                      disabled={oos}
                      className={`px-4 py-2 rounded-full text-sm border transition text-left ${active ? "bg-[#B8860B] text-ivory border-[#B8860B]" : "bg-white hairline hover:border-[#B8860B]"} ${oos ? "opacity-50 cursor-not-allowed line-through" : ""}`}
                      data-testid={`variant-${(v.size || "").replace(/\s+/g,'-')}`}
                    >
                      <span className="font-medium">{v.size}</span>
                      <span className={`ml-2 text-xs ${active ? "opacity-90" : "text-muted-foreground"}`}>₹{v.price}{(v.unit_label || unitLabel) ? ` ${v.unit_label || unitLabel}` : ""}</span>
                    </button>
                  );
                })}
              </div>
              {activeDimensions && (
                <div className="mt-2 text-[11px] text-muted-foreground" data-testid="variant-dimensions">Dimensions: <span className="text-espresso font-medium">{activeDimensions}</span></div>
              )}
              {!activeDimensions && unitLabel && (
                <div className="mt-2 text-[11px] text-muted-foreground">All prices are {unitLabel}.</div>
              )}
            </div>
          )}
          <p className="mt-5 text-sm text-muted-foreground">{p.short_description}</p>
          <div className="mt-5 text-sm">
            {activeStock > 0 ? <span className="text-emerald-700" data-testid="stock-status">Stock Available</span> : <span className="text-destructive" data-testid="stock-status">Out of Stock</span>}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border hairline rounded-full">
              <button onClick={() => setQty(Math.max(1, qty-1))} className="w-11 h-11 flex items-center justify-center text-lg" data-testid="qty-minus">−</button>
              <div className="px-4 py-2 text-sm min-w-[2ch] text-center" data-testid="qty-value">{qty}</div>
              <button onClick={() => setQty(qty+1)} className="w-11 h-11 flex items-center justify-center text-lg" data-testid="qty-plus">+</button>
            </div>
            <button onClick={() => toggleWishlist(p)} className="w-11 h-11 border hairline rounded-full flex items-center justify-center" data-testid="pdp-wishlist" aria-label="Wishlist">
              <Heart className={`w-4 h-4 ${isWishlisted(p.product_id) ? "fill-[#B8860B] text-[#B8860B]" : ""}`} strokeWidth={1.5} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => { addToCart(priceForCart, qty, cartSize, cartDesign, cartVariantId); toast.success("Added to cart"); }} className="btn-outline-gold rounded-full py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-testid="pdp-add-cart">
              <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Add to Cart
            </button>
            <button onClick={buyNow} className="btn-gold rounded-full py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-testid="pdp-buy-now">
              <Zap className="w-4 h-4" strokeWidth={1.5} /> Buy Now
            </button>
          </div>

          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center justify-center gap-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white rounded-full py-3 text-xs uppercase tracking-widest transition" data-testid="pdp-whatsapp-enquire">
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Enquire on WhatsApp
            </a>
          )}
          <div className="mt-3 text-[11px] text-muted-foreground text-center">Prepaid UPI only · No account needed — check out as a guest.</div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-xs text-muted-foreground">
            <div className="flex items-start gap-2"><Truck className="w-4 h-4 gold-text" strokeWidth={1.5} />All-India shipping</div>
            <div className="flex items-start gap-2"><RefreshCw className="w-4 h-4 gold-text" strokeWidth={1.5} />Easy 7-day returns</div>
            <div className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 gold-text" strokeWidth={1.5} />Secure checkout</div>
          </div>

          <Tabs defaultValue="desc" className="mt-10">
            <TabsList className="bg-transparent border-b hairline rounded-none w-full justify-start gap-4 h-auto p-0">
              <TabsTrigger value="desc" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B8860B] data-[state=active]:bg-transparent px-0 py-3" data-testid="tab-desc">Description</TabsTrigger>
              <TabsTrigger value="specs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B8860B] data-[state=active]:bg-transparent px-0 py-3" data-testid="tab-specs">Specifications</TabsTrigger>
              <TabsTrigger value="ship" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B8860B] data-[state=active]:bg-transparent px-0 py-3" data-testid="tab-ship">Shipping</TabsTrigger>
            </TabsList>
            <TabsContent value="desc" className="pt-6 text-sm text-muted-foreground whitespace-pre-line">{p.description}</TabsContent>
            <TabsContent value="specs" className="pt-6 text-sm">
              <table className="w-full">
                <tbody>
                  {Object.entries(p.specifications || {}).map(([k, v]) => (
                    <tr key={k} className="border-b hairline"><td className="py-2 text-muted-foreground w-1/3">{k}</td><td className="py-2">{v}</td></tr>
                  ))}
                  <tr className="border-b hairline"><td className="py-2 text-muted-foreground">Care</td><td className="py-2">{p.care_instructions}</td></tr>
                  <tr className="border-b hairline"><td className="py-2 text-muted-foreground">Package</td><td className="py-2">{p.package_contents}</td></tr>
                </tbody>
              </table>
            </TabsContent>
            <TabsContent value="ship" className="pt-6 text-sm text-muted-foreground space-y-2">
              <p>All-India shipping available with delivery in 3–4 business days.</p>
              <p>Worldwide shipping available — delivery in 10–21 business days.</p>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {related?.length > 0 && (
        <section className="mt-20">
          <h3 className="font-serif text-3xl mb-8">You may also like</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((rp) => <ProductCard key={rp.product_id} product={rp} />)}
          </div>
        </section>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(false)} data-testid="lightbox">
          <button onClick={() => setLightbox(false)} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-ivory flex items-center justify-center hover:bg-white/20" aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <img src={p.images?.[selected]} alt={p.image_alt || p.name} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
          {p.images?.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto px-4" onClick={(e) => e.stopPropagation()}>
              {p.images.map((im, i) => (
                <button key={i} onClick={() => setSelected(i)} className={`w-14 h-14 rounded overflow-hidden border-2 shrink-0 ${i===selected ? "border-[#B8860B]" : "border-white/20"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <StickyBuyBar
        price={activePrice}
        mrp={activeMrp}
        unitLabel={unitLabel}
        variants={variants}
        activeVariantId={activeVariant ? activeVariant.variant_id : null}
        onSelectVariant={(vid) => setSelectedVariantId(vid)}
        qty={qty}
        onQtyChange={setQty}
        outOfStock={activeStock <= 0}
        onBuyNow={buyNow}
        onAddToCart={() => { addToCart(priceForCart, qty, cartSize, cartDesign, cartVariantId); toast.success("Added to cart"); }}
      />
    </div>
  );
}
